import { useState, useEffect, useCallback } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useNavigate } from 'react-router-dom';
import { 
  MapPin, Navigation, DollarSign, Package, CheckCircle, Clock,
  Truck, Phone, User, Star, LogOut, Play, Pause, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';

import { API } from '@/config/api';
import { ASTERPOS_LOGO } from '@/config/brandAssets';

export default function DriverDashboard() {
  const navigate = useNavigate();
  const [driver, setDriver] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('driver_token'));
  const [isOnline, setIsOnline] = useState(false);
  const [availableDeliveries, setAvailableDeliveries] = useState([]);
  const [myDeliveries, setMyDeliveries] = useState([]);
  const [activeDelivery, setActiveDelivery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const getHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  useEffect(() => {
    // Update location periodically when online
    if (isOnline && navigator.geolocation) {
      const watchId = navigator.geolocation.watchPosition(
        async (position) => {
          try {
            await axios.put(
              `${API}/driver/location`,
              {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude
              },
              getHeaders()
            );
          } catch (error) {
            console.error('Failed to update location:', error);
          }
        },
        (error) => console.error('Geolocation error:', error),
        { enableHighAccuracy: true, maximumAge: 10000 }
      );
      return () => navigator.geolocation.clearWatch(watchId);
    }
  }, [isOnline, getHeaders]);

  const fetchDriverProfile = useLatestCallback(async () => {
    try {
      const response = await axios.get(`${API}/driver/me`, getHeaders());
      setDriver(response.data);
      setIsOnline(response.data.status === 'available' || response.data.status === 'on_delivery');
      fetchMyDeliveries();
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      localStorage.removeItem('driver_token');
      setToken(null);
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    if (token) {
      fetchDriverProfile();
    } else {
      setLoading(false);
    }
  }, [token, fetchDriverProfile]);

  const fetchAvailableDeliveries = useLatestCallback(async () => {
    try {
      const response = await axios.get(`${API}/driver/available-deliveries`, getHeaders());
      setAvailableDeliveries(response.data);
    } catch (error) {
      console.error('Failed to fetch deliveries:', error);
    }
  });

  useEffect(() => {
    if (isOnline && token) {
      const interval = setInterval(fetchAvailableDeliveries, 10000);
      fetchAvailableDeliveries();
      return () => clearInterval(interval);
    }
  }, [isOnline, token, fetchAvailableDeliveries]);

  const fetchMyDeliveries = async () => {
    try {
      const response = await axios.get(`${API}/driver/deliveries`, getHeaders());
      setMyDeliveries(response.data);
      // Find active delivery
      const active = response.data.find(d => ['assigned', 'picked_up', 'in_transit'].includes(d.status));
      setActiveDelivery(active || null);
    } catch (error) {
      console.error('Failed to fetch my deliveries:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    try {
      const response = await axios.post(`${API}/driver/login`, loginForm);
      localStorage.setItem('driver_token', response.data.access_token);
      setToken(response.data.access_token);
      setDriver(response.data.driver);
      toast.success('Welcome back!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const toggleOnline = async () => {
    const newStatus = isOnline ? 'offline' : 'available';
    try {
      await axios.put(`${API}/driver/status?status=${newStatus}`, {}, getHeaders());
      setIsOnline(!isOnline);
      toast.success(isOnline ? 'You are now offline' : 'You are now online');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const acceptDelivery = async (deliveryId) => {
    try {
      await axios.post(`${API}/driver/accept-delivery/${deliveryId}`, {}, getHeaders());
      toast.success('Delivery accepted!');
      fetchAvailableDeliveries();
      fetchMyDeliveries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept delivery');
    }
  };

  const updateDeliveryStatus = async (deliveryId, status) => {
    try {
      await axios.put(`${API}/driver/delivery/${deliveryId}/status?status=${status}`, {}, getHeaders());
      toast.success(`Status updated to ${status}`);
      fetchMyDeliveries();
      if (status === 'delivered') {
        fetchDriverProfile();
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('driver_token');
    setToken(null);
    setDriver(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
      </div>
    );
  }

  if (!token) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <img src={ASTERPOS_LOGO} alt="Chain29" className="h-16 w-16 mx-auto rounded-2xl mb-4" />
            <CardTitle>Driver Login</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <input
                type="email"
                placeholder="Email"
                value={loginForm.email}
                onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                className="w-full p-3 border rounded-lg"
                required
              />
              <input
                type="password"
                placeholder="Password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                className="w-full p-3 border rounded-lg"
                required
              />
              <Button type="submit" disabled={isLoggingIn} className="w-full">
                {isLoggingIn ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign In'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30" data-testid="driver-dashboard">
      {/* Header */}
      <header className="bg-card border-b sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={ASTERPOS_LOGO} alt="Chain29" className="h-10 w-10 rounded-xl" />
            <div>
              <h1 className="font-bold">Driver App</h1>
              <p className="text-xs text-muted-foreground">{driver?.full_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm">{isOnline ? 'Online' : 'Offline'}</span>
              <Switch checked={isOnline} onCheckedChange={toggleOnline} />
            </div>
            <Button variant="ghost" size="icon" onClick={handleLogout}>
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <Package className="h-6 w-6 mx-auto mb-2 text-primary" />
              <p className="text-2xl font-bold">{driver?.total_deliveries || 0}</p>
              <p className="text-xs text-muted-foreground">Deliveries</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <DollarSign className="h-6 w-6 mx-auto mb-2 text-green-500" />
              <p className="text-2xl font-bold">${driver?.total_earnings?.toFixed(2) || '0.00'}</p>
              <p className="text-xs text-muted-foreground">Earnings</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <Star className="h-6 w-6 mx-auto mb-2 text-amber-400" />
              <p className="text-2xl font-bold">{driver?.rating?.toFixed(1) || '5.0'}</p>
              <p className="text-xs text-muted-foreground">Rating</p>
            </CardContent>
          </Card>
        </div>

        {/* Active Delivery */}
        {activeDelivery && (
          <Card className="border-primary">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Truck className="h-5 w-5 text-primary" />
                Active Delivery
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-semibold">{activeDelivery.restaurant_name}</p>
                  <p className="text-sm text-muted-foreground">{activeDelivery.restaurant_address}</p>
                </div>
                <Badge>{activeDelivery.status}</Badge>
              </div>
              <div className="bg-muted/50 p-3 rounded-lg">
                <p className="text-sm font-medium mb-1">Deliver to:</p>
                <p className="text-sm">{activeDelivery.customer_name}</p>
                <p className="text-sm text-muted-foreground">{activeDelivery.delivery_address}</p>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Order Total: ${activeDelivery.order_total.toFixed(2)}</span>
                <span className="text-sm font-medium text-green-500">Tip: ${activeDelivery.driver_tip.toFixed(2)}</span>
              </div>
              <div className="flex gap-2">
                {activeDelivery.status === 'assigned' && (
                  <Button onClick={() => updateDeliveryStatus(activeDelivery.id, 'picked_up')} className="flex-1">
                    <Package className="mr-2 h-4 w-4" /> Mark Picked Up
                  </Button>
                )}
                {activeDelivery.status === 'picked_up' && (
                  <Button onClick={() => updateDeliveryStatus(activeDelivery.id, 'in_transit')} className="flex-1">
                    <Navigation className="mr-2 h-4 w-4" /> Start Delivery
                  </Button>
                )}
                {activeDelivery.status === 'in_transit' && (
                  <Button onClick={() => updateDeliveryStatus(activeDelivery.id, 'delivered')} className="flex-1">
                    <CheckCircle className="mr-2 h-4 w-4" /> Mark Delivered
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs for Available/My Deliveries */}
        <Tabs defaultValue={activeDelivery ? "my" : "available"}>
          <TabsList className="w-full">
            <TabsTrigger value="available" className="flex-1">Available ({availableDeliveries.length})</TabsTrigger>
            <TabsTrigger value="my" className="flex-1">My Deliveries</TabsTrigger>
          </TabsList>

          <TabsContent value="available" className="space-y-4">
            {!isOnline ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <p className="text-muted-foreground">Go online to see available deliveries</p>
                </CardContent>
              </Card>
            ) : availableDeliveries.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <Package className="h-12 w-12 mx-auto text-muted-foreground/30 mb-2" />
                  <p className="text-muted-foreground">No available deliveries right now</p>
                </CardContent>
              </Card>
            ) : (
              availableDeliveries.map((delivery) => (
                <Card key={delivery.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-semibold">{delivery.restaurant_name}</p>
                        <p className="text-sm text-muted-foreground">{delivery.items_count} items</p>
                      </div>
                      <span className="text-lg font-bold text-green-500">${delivery.driver_tip.toFixed(2)}</span>
                    </div>
                    <div className="text-sm text-muted-foreground mb-3">
                      <p className="flex items-center gap-1"><MapPin className="h-4 w-4" /> {delivery.delivery_address}</p>
                    </div>
                    <Button onClick={() => acceptDelivery(delivery.id)} className="w-full">
                      Accept Delivery
                    </Button>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="my" className="space-y-4">
            {myDeliveries.filter(d => d.status === 'delivered').length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center">
                  <p className="text-muted-foreground">No completed deliveries yet</p>
                </CardContent>
              </Card>
            ) : (
              myDeliveries.filter(d => d.status === 'delivered').map((delivery) => (
                <Card key={delivery.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium">{delivery.restaurant_name}</p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(delivery.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <Badge variant="secondary">Completed</Badge>
                        <p className="text-sm font-medium text-green-500 mt-1">+${delivery.driver_tip.toFixed(2)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
